<template>
  <v-container fluid class="mkt-page pa-4 pa-md-6">
    <!-- Encabezado -->
    <v-row class="mb-2">
      <v-col cols="12">
        <div class="d-flex flex-wrap align-center justify-space-between">
          <div class="d-flex align-center mb-4 mb-md-0">
            <v-avatar size="56" class="mkt-hero-avatar mr-4" color="blue lighten-5">
              <v-icon color="blue darken-2" size="30">mdi-package-variant-closed</v-icon>
            </v-avatar>
            <div>
              <h1 class="text-h5 text-md-h4 font-weight-bold grey--text text--darken-3 mb-1">
                Material offline
              </h1>
              <p class="text-body-2 grey--text mb-0">
                Inventario de lonas, señalética y activos físicos por sucursal
              </p>
            </div>
          </div>
          <div class="d-flex flex-wrap align-center" style="gap: 8px">
            <v-btn
              v-if="puedeEditar"
              outlined
              color="blue darken-1"
              class="text-capitalize rounded-lg"
              @click="abrirCatalogo"
            >
              <v-icon left small>mdi-book-open-page-variant</v-icon>
              Catálogo
            </v-btn>
            <v-btn
              v-if="puedeEditar"
              color="blue darken-1"
              dark
              depressed
              class="text-capitalize rounded-lg"
              @click="abrirRegistro()"
            >
              <v-icon left small>mdi-plus-circle</v-icon>
              Nuevo registro
            </v-btn>
            <v-btn
              icon
              outlined
              color="grey darken-1"
              class="rounded-lg"
              :loading="cargar"
              @click="cargarRegistros"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- KPIs -->
    <v-row dense class="mb-4">
      <v-col cols="6" sm="3">
        <v-card class="mkt-kpi rounded-lg" flat>
          <v-card-text class="py-3 px-4">
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-caption grey--text text--darken-1">Total registros</div>
                <div class="text-h5 font-weight-bold mkt-stat-total">{{ stats.total }}</div>
              </div>
              <v-icon class="mkt-kpi-icon mkt-stat-total">mdi-clipboard-list</v-icon>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3">
        <v-card class="mkt-kpi rounded-lg" flat>
          <v-card-text class="py-3 px-4">
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-caption grey--text text--darken-1">FAST</div>
                <div class="text-h5 font-weight-bold mkt-stat-fast">{{ stats.fast }}</div>
              </div>
              <v-icon class="mkt-kpi-icon mkt-stat-fast">mdi-flash</v-icon>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3">
        <v-card class="mkt-kpi rounded-lg" flat>
          <v-card-text class="py-3 px-4">
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-caption grey--text text--darken-1">Oficina</div>
                <div class="text-h5 font-weight-bold mkt-stat-oficina">{{ stats.oficina }}</div>
              </div>
              <v-icon class="mkt-kpi-icon mkt-stat-oficina">mdi-office-building</v-icon>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3">
        <v-card class="mkt-kpi rounded-lg" flat>
          <v-card-text class="py-3 px-4">
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-caption grey--text text--darken-1">Mant. próximo</div>
                <div class="text-h5 font-weight-bold mkt-stat-warm">{{ stats.mantenimiento }}</div>
              </div>
              <v-icon class="mkt-kpi-icon mkt-stat-warm">mdi-wrench-clock</v-icon>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tabla principal -->
    <v-row>
      <v-col cols="12">
        <v-card class="mkt-panel rounded-lg" elevation="0">
          <div class="mkt-panel-toolbar px-4 px-md-5 py-3 d-flex flex-wrap align-center justify-space-between">
            <v-chip-group
              v-model="filtroTipo"
              mandatory
              class="mkt-filter-chips"
            >
              <v-chip filter value="" class="mkt-filter-chip">Todos</v-chip>
              <v-chip filter value="FAST" class="mkt-filter-chip">FAST</v-chip>
              <v-chip filter value="OFICINA" class="mkt-filter-chip">Oficina</v-chip>
            </v-chip-group>
            <v-text-field
              v-model="search"
              placeholder="Buscar sucursal, activo, proveedor..."
              prepend-inner-icon="mdi-magnify"
              filled
              dense
              hide-details
              rounded
              class="mkt-search mt-3 mt-md-0"
              clearable
            ></v-text-field>
          </div>

          <v-divider></v-divider>

          <v-data-table
            :headers="headers"
            :items="registros"
            :search="search"
            dense
            elevation-0
            item-key="id_registro"
            class="mkt-table"
            :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }"
          >
            <template v-slot:item.escuela="{ item }">
              <v-chip
                x-small
                class="font-weight-medium px-2 mkt-chip-tipo"
                :class="item.escuela === 'FAST' ? 'mkt-chip-fast' : 'mkt-chip-oficina'"
              >
                {{ item.escuela }}
              </v-chip>
            </template>

            <template v-slot:item.sucursal="{ item }">
              <div class="font-weight-medium text-body-2">{{ item.sucursal }}</div>
            </template>

            <template v-slot:item.activo="{ item }">
              <div class="text-body-2 font-weight-medium">{{ item.activo }}</div>
            </template>

            <template v-slot:header.archivo_url="{ header }">
              <div class="d-inline-flex align-center">
                <span>{{ header.text }}</span>
                <v-menu
                  offset-y
                  :close-on-content-click="false"
                  max-width="380"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      x-small
                      color="blue darken-1"
                      class="ml-1"
                      v-bind="attrs"
                      v-on="on"
                      aria-label="Ayuda para enlace de Drive"
                      @click.stop
                    >
                      <v-icon small>mdi-help-circle-outline</v-icon>
                    </v-btn>
                  </template>
                  <v-card class="pa-4">
                    <div class="text-subtitle-2 font-weight-medium mb-2 d-flex align-center">
                      <v-icon small color="blue darken-1" class="mr-2">mdi-google-drive</v-icon>
                      Enlace del archivo en Drive
                    </div>
                    <p
                      v-for="(p, i) in ayudaLinkDriveParrafos"
                      :key="'ayuda-principal-' + i"
                      class="text-body-2 grey--text text--darken-2 mb-2"
                      style="line-height: 1.45"
                    >
                      {{ p }}
                    </p>
                  </v-card>
                </v-menu>
              </div>
            </template>

            <template v-slot:item.archivo_url="{ item }">
              <v-btn
                v-if="item.archivo_url"
                x-small
                depressed
                rounded
                color="blue lighten-5"
                class="blue--text text--darken-2"
                :href="linkDrive(item.archivo_url)"
                target="_blank"
                rel="noopener noreferrer"
                @click.stop
              >
                <v-icon left x-small>
                  {{ item.archivo_tipo === 'pdf' ? 'mdi-file-pdf-box' : 'mdi-google-drive' }}
                </v-icon>
                Ver
              </v-btn>
              <span v-else class="caption grey--text">Sin archivo</span>
            </template>

            <template v-slot:item.medidas="{ item }">
              <span class="text-body-2">{{ item.medidas || '—' }}</span>
            </template>

            <template v-slot:item.precio="{ item }">
              <span class="font-weight-medium mkt-text-price">{{ formatearPrecio(item.precio) }}</span>
            </template>

            <template v-slot:item.fecha_siguiente_mantenimiento="{ item }">
              <v-chip
                v-if="item.fecha_siguiente_mantenimiento"
                x-small
                class="mkt-chip-mant"
                :class="{ 'mkt-chip-mant--soon': mantenimientoUrgente(item.fecha_siguiente_mantenimiento) }"
              >
                {{ item.fecha_siguiente_mantenimiento }}
              </v-chip>
              <span v-else class="caption grey--text">—</span>
            </template>

            <template v-slot:item.nota="{ item }">
              <span class="text-truncate d-inline-block mkt-nota-cell" :title="item.nota">{{ item.nota || '—' }}</span>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-tooltip v-if="puedeEditar" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small color="blue darken-1" v-bind="attrs" v-on="on" @click="abrirRegistro(item)">
                    <v-icon small>mdi-pencil-outline</v-icon>
                  </v-btn>
                </template>
                <span>Editar</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small color="blue lighten-1" v-bind="attrs" v-on="on" @click="abrirHistorial(item)">
                    <v-icon small>mdi-history</v-icon>
                  </v-btn>
                </template>
                <span>Historial</span>
              </v-tooltip>
            </template>

            <template v-slot:no-data>
              <div class="text-center py-10 px-4">
                <v-icon size="64" color="grey lighten-1">mdi-package-variant</v-icon>
                <div class="text-h6 grey--text text--darken-1 mt-3">Sin registros todavía</div>
                <p class="text-body-2 grey--text mb-4">
                  {{ puedeEditar
                    ? 'Agrega ubicaciones en catálogo y crea tu primer registro operativo.'
                    : 'No hay registros. Solo usuarios autorizados pueden crear o editar datos.' }}
                </p>
                <v-btn
                  v-if="puedeEditar"
                  color="blue darken-1"
                  dark
                  depressed
                  rounded
                  class="text-capitalize"
                  @click="abrirRegistro()"
                >
                  <v-icon left>mdi-plus</v-icon>
                  Crear registro
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Modal registro operativo -->
    <v-dialog v-model="dialogRegistro" max-width="720px" persistent scrollable>
      <v-card class="rounded-lg overflow-hidden">
        <div class="mkt-dialog-header px-5 py-4 d-flex align-center">
          <v-icon color="white" class="mr-3">mdi-clipboard-edit-outline</v-icon>
          <span class="white--text text-h6 font-weight-medium">
            {{ registroIndex === -1 ? 'Nuevo registro' : 'Editar registro' }}
          </span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="cerrarRegistro"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <v-card-text class="pt-5 px-5">
          <div class="text-overline grey--text mb-3">Asignación</div>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-select
                v-model="registroItem.id_escuela"
                :items="escuelasOpciones"
                item-text="label"
                item-value="id_escuela"
                label="Escuela *"
                filled
                dense
                rounded
                hide-details
                prepend-inner-icon="mdi-school"
                class="mb-4"
                @change="onEscuelaRegistroChange"
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="registroItem.id_activo"
                :items="activosFiltradosRegistro"
                item-text="label"
                item-value="id_activo"
                label="Activo *"
                filled
                dense
                rounded
                hide-details
                prepend-inner-icon="mdi-image-area"
                class="mb-4"
                :disabled="!registroItem.id_escuela"
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="registroItem.id_proveedor"
                :items="proveedoresOpciones"
                item-text="label"
                item-value="id_proveedor"
                label="Proveedor"
                filled
                dense
                rounded
                hide-details
                clearable
                prepend-inner-icon="mdi-truck-delivery"
                class="mb-4"
              ></v-select>
            </v-col>
          </v-row>

          <v-divider class="my-2"></v-divider>
          <div class="text-overline grey--text mb-3 mt-2">Operación y costos</div>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="registroItem.fecha_ultimo_cambio"
                label="Fecha último cambio"
                type="date"
                filled
                dense
                rounded
                hide-details
                class="mb-4"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="registroItem.precio"
                label="Precio"
                type="number"
                prefix="$"
                filled
                dense
                rounded
                hide-details
                class="mb-4"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="registroItem.ultimo_cambio"
                label="Descripción del último cambio"
                filled
                dense
                rounded
                hide-details
                class="mb-4"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="registroItem.fecha_siguiente_mantenimiento"
                label="Siguiente mantenimiento"
                type="date"
                filled
                dense
                rounded
                hide-details
                class="mb-4"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="registroItem.fecha_garantia"
                label="Fecha garantía"
                type="date"
                filled
                dense
                rounded
                hide-details
                class="mb-4"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="registroItem.nota"
                label="Nota"
                filled
                dense
                rounded
                hide-details
                auto-grow
                rows="2"
                class="mb-2"
              ></v-textarea>
            </v-col>
            <v-col cols="12" v-if="registroIndex > -1">
              <v-text-field
                v-model="registroItem.comentario_historial"
                label="Comentario para historial (opcional)"
                filled
                dense
                rounded
                hide-details
                prepend-inner-icon="mdi-comment-text-outline"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="px-5 py-3">
          <v-spacer></v-spacer>
          <v-btn text rounded class="text-capitalize" @click="cerrarRegistro">Cancelar</v-btn>
          <v-btn color="blue darken-1" dark depressed rounded class="text-capitalize px-6" @click="guardarRegistro">
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal catálogo -->
    <v-dialog v-model="dialogCatalogo" max-width="960px" persistent scrollable>
      <v-card class="rounded-lg overflow-hidden">
        <div class="mkt-dialog-header mkt-dialog-header--catalog px-5 py-4 d-flex align-center">
          <v-icon color="white" class="mr-3">mdi-book-open-page-variant</v-icon>
          <span class="white--text text-h6 font-weight-medium">Catálogo</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="dialogCatalogo = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <v-tabs v-model="tabCatalogo" background-color="blue lighten-5" color="blue darken-1" grow show-arrows>
          <v-tab class="text-capitalize font-weight-medium">
            <v-icon left small>mdi-school</v-icon> Escuelas
          </v-tab>
          <v-tab class="text-capitalize font-weight-medium">
            <v-icon left small>mdi-image-area</v-icon> Activos
          </v-tab>
          <v-tab class="text-capitalize font-weight-medium">
            <v-icon left small>mdi-truck</v-icon> Proveedores
          </v-tab>
        </v-tabs>
        <v-card-text class="pa-0" style="max-height: 70vh">
          <v-tabs-items v-model="tabCatalogo">
            <!-- Escuelas -->
            <v-tab-item class="pa-4">
              <div class="d-flex justify-end mb-3">
                <v-btn color="blue darken-1" dark depressed rounded small class="text-capitalize" @click="nuevaEscuela">
                  <v-icon left small>mdi-plus</v-icon>
                  Nueva escuela
                </v-btn>
              </div>
              <v-data-table
                :headers="headersEscuelas"
                :items="escuelas"
                dense
                class="mkt-table mkt-table--nested rounded-lg"
                item-key="id_escuela"
              >
                <template v-slot:item.tipo="{ item }">
                  <v-chip x-small class="mkt-chip-tipo" :class="item.tipo === 'FAST' ? 'mkt-chip-fast' : 'mkt-chip-oficina'">{{ item.tipo }}</v-chip>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn icon small color="blue darken-1" @click="editarEscuela(item)"><v-icon small>mdi-pencil-outline</v-icon></v-btn>
                </template>
              </v-data-table>

              <v-dialog v-model="dialogEscuela" max-width="550px" persistent>
                <v-card>
                  <v-card-title>{{ escuelaIndex === -1 ? 'Nueva escuela' : 'Editar escuela' }}</v-card-title>
                  <v-card-text>
                    <v-select
                      v-model="escuelaItem.tipo"
                      :items="['FAST', 'OFICINA']"
                      label="* Tipo"
                      filled
                      dense
                      hide-details
                      class="mb-3"
                      @change="escuelaItem.id_plantel = null"
                    ></v-select>
                    <v-autocomplete
                      v-model="escuelaItem.id_plantel"
                      :items="plantelesFiltrados"
                      item-text="plantel"
                      item-value="id_plantel"
                      label="* Plantel / sucursal"
                      filled
                      dense
                      hide-details
                      class="mb-3"
                      :no-data-text="escuelaIndex === -1 ? 'No hay planteles disponibles (todos ya están registrados)' : 'Sin planteles'"
                      @change="onPlantelSeleccionado"
                    ></v-autocomplete>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="dialogEscuela = false">Cancelar</v-btn>
                    <v-btn color="blue darken-1" dark depressed rounded class="text-capitalize" @click="guardarEscuela">Guardar</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-tab-item>

            <!-- Activos -->
            <v-tab-item class="pa-4">
              <div class="d-flex flex-wrap align-center justify-space-between mb-3" style="gap: 8px">
                <div class="d-flex align-center">
                  <span class="text-body-2 grey--text text--darken-1 mr-1">Link de Drive</span>
                  <v-menu
                    offset-y
                    :close-on-content-click="false"
                    max-width="380"
                    left
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        x-small
                        color="blue darken-1"
                        class="ml-n1"
                        v-bind="attrs"
                        v-on="on"
                        aria-label="Ayuda para enlace de Drive"
                      >
                        <v-icon small>mdi-help-circle-outline</v-icon>
                      </v-btn>
                    </template>
                    <v-card class="pa-4">
                      <div class="text-subtitle-2 font-weight-medium mb-2 d-flex align-center">
                        <v-icon small color="blue darken-1" class="mr-2">mdi-google-drive</v-icon>
                        Enlace del archivo en Drive
                      </div>
                      <p
                        v-for="(p, i) in ayudaLinkDriveParrafos"
                        :key="'ayuda-tabla-' + i"
                        class="text-body-2 grey--text text--darken-2 mb-2"
                        style="line-height: 1.45"
                      >
                        {{ p }}
                      </p>
                    </v-card>
                  </v-menu>
                </div>
                <v-btn color="blue darken-1" dark depressed rounded small class="text-capitalize" @click="nuevoActivo">
                  <v-icon left small>mdi-plus</v-icon>
                  Nuevo activo
                </v-btn>
              </div>
              <v-data-table
                :headers="headersActivos"
                :items="activos"
                dense
                class="mkt-table mkt-table--nested rounded-lg"
                item-key="id_activo"
              >
                    <template v-slot:header.archivo_url="{ header }">
                      <div class="d-inline-flex align-center">
                        <span>{{ header.text }}</span>
                        <v-menu
                          offset-y
                          :close-on-content-click="false"
                          max-width="380"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              icon
                              x-small
                              color="blue darken-1"
                              class="ml-1"
                              v-bind="attrs"
                              v-on="on"
                              aria-label="Ayuda para enlace de Drive"
                              @click.stop
                            >
                              <v-icon small>mdi-help-circle-outline</v-icon>
                            </v-btn>
                          </template>
                          <v-card class="pa-4">
                            <div class="text-subtitle-2 font-weight-medium mb-2 d-flex align-center">
                              <v-icon small color="blue darken-1" class="mr-2">mdi-google-drive</v-icon>
                              Enlace del archivo en Drive
                            </div>
                            <p
                              v-for="(p, i) in ayudaLinkDriveParrafos"
                              :key="'ayuda-header-' + i"
                              class="text-body-2 grey--text text--darken-2 mb-2"
                              style="line-height: 1.45"
                            >
                              {{ p }}
                            </p>
                          </v-card>
                        </v-menu>
                      </div>
                    </template>
                    <template v-slot:item.archivo_url="{ item }">
                      <v-btn
                        v-if="item.archivo_url"
                        x-small
                        depressed
                        rounded
                        color="blue lighten-5"
                        class="blue--text text--darken-2"
                        :href="linkDrive(item.archivo_url)"
                        target="_blank"
                        rel="noopener noreferrer"
                        @click.stop
                      >
                        <v-icon left x-small>mdi-google-drive</v-icon>
                        Drive
                      </v-btn>
                      <span v-else class="caption grey--text">—</span>
                    </template>
                    <template v-slot:item.actions="{ item }">
                      <v-btn icon small color="blue darken-1" @click="editarActivo(item)"><v-icon small>mdi-pencil-outline</v-icon></v-btn>
                    </template>
                  </v-data-table>

              <v-dialog v-model="dialogActivo" max-width="550px" persistent>
                <v-card>
                  <v-card-title>{{ activoIndex === -1 ? 'Nuevo activo' : 'Editar activo' }}</v-card-title>
                  <v-card-text>
                    <v-select
                      v-model="activoItem.id_escuela"
                      :items="escuelasOpciones"
                      item-text="label"
                      item-value="id_escuela"
                      label="* Escuela"
                      filled
                      dense
                      hide-details
                      class="mb-3"
                    ></v-select>
                    <v-text-field v-model="activoItem.nombre" label="* Nombre" filled dense hide-details class="mb-3"></v-text-field>
                    <v-text-field v-model="activoItem.medidas" label="Medidas" filled dense hide-details class="mb-3"></v-text-field>
                    <v-select
                      v-model="activoItem.archivo_tipo"
                      :items="tiposArchivo"
                      item-text="text"
                      item-value="value"
                      label="Tipo de archivo"
                      filled
                      dense
                      hide-details
                      class="mb-3"
                    ></v-select>
                    <div class="d-flex align-center mb-1">
                      <span class="text-caption grey--text text--darken-1">Link de Google Drive</span>
                      <v-menu
                        offset-y
                        :close-on-content-click="false"
                        max-width="380"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            x-small
                            color="blue darken-1"
                            class="ml-1"
                            v-bind="attrs"
                            v-on="on"
                            aria-label="Ayuda para enlace de Drive"
                          >
                            <v-icon small>mdi-help-circle-outline</v-icon>
                          </v-btn>
                        </template>
                        <v-card class="pa-4">
                          <div class="text-subtitle-2 font-weight-medium mb-2 d-flex align-center">
                            <v-icon small color="blue darken-1" class="mr-2">mdi-google-drive</v-icon>
                            Enlace del archivo en Drive
                          </div>
                          <p
                            v-for="(p, i) in ayudaLinkDriveParrafos"
                            :key="'ayuda-form-' + i"
                            class="text-body-2 grey--text text--darken-2 mb-2"
                            style="line-height: 1.45"
                          >
                            {{ p }}
                          </p>
                        </v-card>
                      </v-menu>
                    </div>
                    <v-text-field
                      v-model="activoItem.archivo_url"
                      label="Pegue el vínculo del archivo (imagen o PDF)"
                      placeholder="https://drive.google.com/file/d/..."
                      filled
                      dense
                      hide-details="auto"
                      prepend-inner-icon="mdi-google-drive"
                      hint="Use el enlace del archivo abierto, no el de la carpeta"
                      persistent-hint
                      class="mb-3"
                    ></v-text-field>
                    <v-text-field
                      v-if="activoIndex > -1"
                      v-model="activoItem.comentario_historial"
                      label="Comentario para historial (opcional)"
                      filled
                      dense
                      hide-details
                      class="mb-3"
                    ></v-text-field>

                    <template v-if="activoIndex === -1">
                      <v-divider class="my-3"></v-divider>
                      <div class="text-subtitle-2 mb-2">¿Agregar proveedor?</div>
                      <v-btn-toggle v-model="asignarProveedorActivo" mandatory dense class="mb-3">
                        <v-btn :value="false" small>No</v-btn>
                        <v-btn :value="true" small>Sí</v-btn>
                      </v-btn-toggle>
                      <template v-if="asignarProveedorActivo">
                        <v-select
                          v-model="activoItem.id_proveedor_existente"
                          :items="proveedoresOpciones"
                          item-text="label"
                          item-value="id_proveedor"
                          label="Seleccionar proveedor existente"
                          filled
                          dense
                          hide-details
                          clearable
                          class="mb-2"
                        ></v-select>
                        <div class="text-caption mb-2">O crear nuevo:</div>
                        <v-text-field v-model="proveedorWizard.nombre" label="Nombre proveedor" filled dense hide-details class="mb-2"></v-text-field>
                        <v-text-field v-model="proveedorWizard.telefono" label="Teléfono" filled dense hide-details class="mb-2"></v-text-field>
                        <v-text-field v-model="proveedorWizard.numero_cuenta" label="Número de cuenta" filled dense hide-details class="mb-2"></v-text-field>
                        <v-textarea v-model="proveedorWizard.nota" label="Nota" filled dense hide-details rows="2"></v-textarea>
                      </template>
                    </template>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="dialogActivo = false">Cancelar</v-btn>
                    <v-btn color="blue darken-1" dark depressed rounded class="text-capitalize" @click="guardarActivo">Guardar</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-tab-item>

            <!-- Proveedores -->
            <v-tab-item class="pa-4">
              <div class="d-flex justify-end mb-3">
                <v-btn color="blue darken-1" dark depressed rounded small class="text-capitalize" @click="nuevoProveedor">
                  <v-icon left small>mdi-plus</v-icon>
                  Nuevo proveedor
                </v-btn>
              </div>
              <v-data-table
                :headers="headersProveedores"
                :items="proveedores"
                dense
                class="mkt-table mkt-table--nested rounded-lg"
                item-key="id_proveedor"
              >
                <template v-slot:item.actions="{ item }">
                  <v-btn icon small color="blue darken-1" @click="editarProveedor(item)"><v-icon small>mdi-pencil-outline</v-icon></v-btn>
                </template>
              </v-data-table>

              <v-dialog v-model="dialogProveedor" max-width="500px" persistent>
                <v-card>
                  <v-card-title>{{ proveedorIndex === -1 ? 'Nuevo proveedor' : 'Editar proveedor' }}</v-card-title>
                  <v-card-text>
                    <v-text-field v-model="proveedorItem.nombre" label="* Nombre" filled dense hide-details class="mb-3"></v-text-field>
                    <v-text-field v-model="proveedorItem.telefono" label="Teléfono" filled dense hide-details class="mb-3"></v-text-field>
                    <v-text-field v-model="proveedorItem.numero_cuenta" label="Número de cuenta" filled dense hide-details class="mb-3"></v-text-field>
                    <v-textarea v-model="proveedorItem.nota" label="Nota" filled dense hide-details rows="2"></v-textarea>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="dialogProveedor = false">Cancelar</v-btn>
                    <v-btn color="blue darken-1" dark depressed rounded class="text-capitalize" @click="guardarProveedor">Guardar</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Modal historial -->
    <v-dialog v-model="dialogHistorial" max-width="720px" scrollable content-class="mkt-historial-dialog">
      <v-card class="rounded-lg overflow-hidden mkt-historial-card">
        <div class="mkt-dialog-header mkt-dialog-header--history px-5 py-4">
          <div class="d-flex align-center">
            <v-avatar size="44" color="rgba(255,255,255,0.2)" class="mr-3">
              <v-icon color="white">mdi-timeline-clock-outline</v-icon>
            </v-avatar>
            <div>
              <div class="white--text text-h6 font-weight-medium">Historial de cambios</div>
              <div class="white--text text-caption" style="opacity: 0.9">
                {{ historial.length }} {{ historial.length === 1 ? 'movimiento' : 'movimientos' }} registrados
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-btn icon dark @click="dialogHistorial = false"><v-icon>mdi-close</v-icon></v-btn>
          </div>
        </div>

        <!-- Resumen del registro -->
        <div v-if="historialRegistro" class="mkt-historial-summary px-5 py-4">
          <v-row dense align="center">
            <v-col cols="12" sm="auto">
              <v-chip
                small
                class="mkt-chip-tipo"
                :class="historialRegistro.escuela === 'FAST' ? 'mkt-chip-fast' : 'mkt-chip-oficina'"
              >
                {{ historialRegistro.escuela }}
              </v-chip>
            </v-col>
            <v-col cols="12" sm>
              <div class="text-subtitle-1 font-weight-bold blue--text text--darken-3">
                {{ historialRegistro.sucursal }}
              </div>
              <div class="text-body-2 grey--text text--darken-1">
                <v-icon x-small class="mr-1">mdi-image-area</v-icon>
                {{ historialRegistro.activo }}
                <span v-if="historialRegistro.medidas" class="ml-2">· {{ historialRegistro.medidas }}</span>
              </div>
            </v-col>
            <v-col cols="12" sm="auto" class="text-sm-right">
              <div v-if="historialRegistro.proveedor" class="caption grey--text">Proveedor</div>
              <div class="text-body-2 font-weight-medium">{{ historialRegistro.proveedor || '—' }}</div>
              <div class="text-caption blue--text text--darken-2 mt-1">{{ formatearPrecio(historialRegistro.precio) }}</div>
            </v-col>
          </v-row>
        </div>

        <v-divider></v-divider>

        <v-card-text class="pa-5 mkt-historial-body">
          <div v-if="!historial.length" class="mkt-historial-empty text-center py-8">
            <v-icon size="56" color="blue lighten-3">mdi-clipboard-text-clock-outline</v-icon>
            <div class="text-h6 grey--text text--darken-1 mt-3">Aún no hay cambios</div>
            <p class="text-body-2 grey--text mb-0">
              Las ediciones de precio, fechas y archivos aparecerán aquí automáticamente.
            </p>
          </div>

          <div v-else class="mkt-timeline">
            <div
              v-for="(item, idx) in historial"
              :key="item.id_historial"
              class="mkt-timeline-item"
              :class="{ 'mkt-timeline-item--last': idx === historial.length - 1 }"
            >
              <div class="mkt-timeline-rail">
                <div class="mkt-timeline-dot" :class="'mkt-timeline-dot--' + tipoHistorial(item)">
                  <v-icon x-small color="white">{{ iconoHistorial(item) }}</v-icon>
                </div>
                <div v-if="idx < historial.length - 1" class="mkt-timeline-line"></div>
              </div>

              <v-card class="mkt-timeline-card rounded-lg" outlined>
                <div class="d-flex flex-wrap align-center justify-space-between mb-2">
                  <v-chip x-small class="mkt-historial-campo" color="blue lighten-5">
                    <span class="blue--text text--darken-2 font-weight-medium">{{ item.campo || 'Actualización' }}</span>
                  </v-chip>
                  <span class="caption grey--text">
                    <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                    {{ formatearFechaHistorial(item.fecha_cambio) }}
                  </span>
                </div>

                <div
                  v-if="item.valor_anterior || item.valor_nuevo"
                  class="mkt-historial-diff mb-3"
                >
                  <div v-if="item.valor_anterior" class="mkt-diff-box mkt-diff-box--old">
                    <div class="mkt-diff-label">Antes</div>
                    <div class="mkt-diff-value">{{ formatearValorHistorial(item.valor_anterior, item.campo) }}</div>
                  </div>
                  <v-icon v-if="item.valor_anterior && item.valor_nuevo" small color="blue lighten-1" class="mx-2 flex-shrink-0">
                    mdi-arrow-right
                  </v-icon>
                  <div v-if="item.valor_nuevo" class="mkt-diff-box mkt-diff-box--new">
                    <div class="mkt-diff-label">Después</div>
                    <div class="mkt-diff-value">{{ formatearValorHistorial(item.valor_nuevo, item.campo) }}</div>
                  </div>
                </div>

                <v-btn
                  v-if="item.archivo_url"
                  x-small
                  depressed
                  rounded
                  color="blue lighten-5"
                  class="blue--text text--darken-2 mb-2"
                  :href="linkDrive(item.archivo_url)"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <v-icon left x-small>mdi-google-drive</v-icon>
                  Ver archivo en Drive
                </v-btn>

                <div class="d-flex align-start mt-2">
                  <v-avatar size="28" color="blue lighten-4" class="mr-2 flex-shrink-0">
                    <span class="blue--text text--darken-2 caption font-weight-bold">
                      {{ inicialesUsuario(item.nombre_usuario) }}
                    </span>
                  </v-avatar>
                  <div class="flex-grow-1 min-width-0">
                    <div class="text-body-2 font-weight-medium blue-grey--text text--darken-2">
                      {{ item.nombre_usuario || 'Usuario' }}
                    </div>
                    <div v-if="item.comentario" class="text-caption grey--text text--darken-1 mkt-historial-comentario">
                      "{{ item.comentario }}"
                    </div>
                  </div>
                </div>
              </v-card>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <Alerta v-if="respuestaAlerta" :parametros="parametros" />
    <carga v-if="cargar" />
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import Alerta from '@/components/alertas/Alerta.vue'
import carga from '@/components/alertas/carga.vue'
import validarErrores from '@/mixins/validarErrores'

/** Únicos iderp que pueden crear/editar registros y catálogo en Material offline */
const EDITORES_USUARIOS = [32, 3042]

const defaultRegistro = () => ({
  id_registro: 0,
  id_escuela: null,
  id_activo: null,
  id_proveedor: null,
  fecha_ultimo_cambio: null,
  ultimo_cambio: '',
  precio: null,
  fecha_siguiente_mantenimiento: null,
  fecha_garantia: null,
  nota: '',
  comentario_historial: '',
})

export default {
  name: 'MaterialOffline',
  mixins: [validarErrores],
  components: { Alerta, carga },

  data: () => ({
    parametros: { dialogError: false, mensaje: '', color: '' },
    respuestaAlerta: false,
    cargar: false,
    search: '',
    filtroTipo: '',
    registros: [],
    escuelas: [],
    activos: [],
    proveedores: [],
    planteles: [],
    historial: [],
    historialRegistro: null,

    dialogRegistro: false,
    registroIndex: -1,
    registroItem: defaultRegistro(),

    dialogCatalogo: false,
    tabCatalogo: 0,

    dialogEscuela: false,
    escuelaIndex: -1,
    escuelaItem: { id_escuela: 0, tipo: 'FAST', id_plantel: null, nombre_sucursal: '', activo_sn: 1 },

    dialogActivo: false,
    activoIndex: -1,
    activoItem: { id_activo: 0, id_escuela: null, nombre: '', medidas: '', archivo_url: '', archivo_tipo: 'foto', activo_sn: 1, id_proveedor_existente: null, comentario_historial: '' },
    asignarProveedorActivo: false,
    proveedorWizard: { nombre: '', telefono: '', numero_cuenta: '', nota: '' },

    dialogProveedor: false,
    proveedorIndex: -1,
    proveedorItem: { id_proveedor: 0, nombre: '', telefono: '', numero_cuenta: '', nota: '', activo_sn: 1 },

    dialogHistorial: false,
    apiOfflineDisponible: true,
    avisoApiOffline: false,

    tiposArchivo: [
      { text: 'Imagen', value: 'foto' },
      { text: 'PDF', value: 'pdf' },
    ],

    headers: [
      { text: 'Escuela', value: 'escuela', width: 90 },
      { text: 'Sucursal', value: 'sucursal' },
      { text: 'Activo', value: 'activo' },
      { text: 'Archivo', value: 'archivo_url', sortable: false, width: 100 },
      { text: 'Medidas', value: 'medidas', width: 100 },
      { text: 'Últ. cambio', value: 'fecha_ultimo_cambio', width: 110 },
      { text: 'Descripción', value: 'ultimo_cambio' },
      { text: 'Proveedor', value: 'proveedor' },
      { text: 'Precio', value: 'precio', align: 'end', width: 100 },
      { text: 'Mantenimiento', value: 'fecha_siguiente_mantenimiento', width: 120 },
      { text: 'Garantía', value: 'fecha_garantia', width: 110 },
      { text: 'Nota', value: 'nota', width: 140 },
      { text: '', value: 'actions', sortable: false, width: 90 },
    ],
    headersEscuelas: [
      { text: 'Tipo', value: 'tipo' },
      { text: 'Sucursal', value: 'nombre_sucursal' },
      { text: 'ID plantel', value: 'id_plantel' },
      { text: '', value: 'actions', sortable: false, width: 60 },
    ],
    headersActivos: [
      { text: 'Escuela', value: 'nombre_sucursal' },
      { text: 'Tipo', value: 'tipo' },
      { text: 'Activo', value: 'nombre' },
      { text: 'Medidas', value: 'medidas' },
      { text: 'Drive', value: 'archivo_url', sortable: false },
      { text: '', value: 'actions', sortable: false, width: 60 },
    ],
    headersProveedores: [
      { text: 'Nombre', value: 'nombre' },
      { text: 'Teléfono', value: 'telefono' },
      { text: 'Cuenta', value: 'numero_cuenta' },
      { text: 'Nota', value: 'nota' },
      { text: '', value: 'actions', sortable: false, width: 60 },
    ],
  }),

  computed: {
    ...mapGetters('login', ['getdatosUsuarioERPERP']),

    puedeEditar() {
      const id = this.resolveIdUsuario()
      return id != null && EDITORES_USUARIOS.includes(id)
    },

    puedeEditarCatalogo() {
      return this.puedeEditar
    },

    ayudaLinkDriveParrafos() {
      return [
        'Registre el vínculo del archivo (imagen o PDF), no el de la carpeta donde se encuentra almacenado.',
        'Abra el archivo en Google Drive, use la opción Compartir o Copiar vínculo del archivo abierto y péguelo en el campo correspondiente. También puede copiar el enlace desde el menú del archivo (clic derecho → Obtener vínculo / Copiar vínculo).',
        'Si se utiliza el enlace de la carpeta, no será posible visualizar correctamente el contenido del activo.',
      ]
    },

    idUsuario() {
      return this.resolveIdUsuario()
    },

    escuelasOpciones() {
      return this.escuelas.map((e) => ({
        ...e,
        label: `${e.tipo} - ${e.nombre_sucursal}`,
      }))
    },

    proveedoresOpciones() {
      return this.proveedores.map((p) => ({
        ...p,
        label: p.nombre,
      }))
    },

    activosFiltradosRegistro() {
      if (!this.registroItem.id_escuela) return []
      return this.activos
        .filter((a) => Number(a.id_escuela) === Number(this.registroItem.id_escuela))
        .map((a) => ({ ...a, label: a.nombre }))
    },

    plantelesFiltrados() {
      let lista = this.planteles
      if (this.escuelaItem.tipo === 'FAST') {
        lista = lista.filter((p) => (p.plantel || '').toUpperCase().includes('FAST'))
      } else if (this.escuelaItem.tipo === 'OFICINA') {
        lista = lista.filter((p) => !(p.plantel || '').toUpperCase().includes('FAST'))
      }
      if (this.escuelaIndex === -1) {
        const idsUsados = new Set(
          this.escuelas
            .filter((e) => e.tipo === this.escuelaItem.tipo)
            .map((e) => Number(e.id_plantel))
        )
        lista = lista.filter((p) => !idsUsados.has(Number(p.id_plantel)))
      }
      return lista
    },

    stats() {
      const hoy = new Date()
      hoy.setHours(0, 0, 0, 0)
      const en30 = new Date(hoy)
      en30.setDate(en30.getDate() + 30)
      let fast = 0
      let oficina = 0
      let mantenimiento = 0
      this.registros.forEach((r) => {
        if (r.escuela === 'FAST') fast++
        if (r.escuela === 'OFICINA') oficina++
        if (r.fecha_siguiente_mantenimiento) {
          const f = new Date(r.fecha_siguiente_mantenimiento + 'T00:00:00')
          if (!isNaN(f.getTime()) && f >= hoy && f <= en30) mantenimiento++
        }
      })
      return {
        total: this.registros.length,
        fast,
        oficina,
        mantenimiento,
      }
    },
  },

  watch: {
    filtroTipo() {
      this.cargarRegistros()
    },
  },

  async created() {
    await this.inicializar()
  },

  methods: {
    resolveIdUsuario() {
      const u = this.getdatosUsuarioERPERP
      if (!u) return null
      const n = Number(u.id_usuario != null ? u.id_usuario : (u.iderp != null ? u.iderp : u.idusuario))
      return Number.isFinite(n) && n > 0 ? n : null
    },

    errMsgApi(err, fallback) {
      const status = err && err.response && err.response.status
      if (status === 404) {
        return 'El API de Material offline no está desplegado en este servidor. Solicita a sistemas el deploy de api-rh (ticket 71989) y reinicio de PM2.'
      }
      return (
        (err.response && err.response.data && (err.response.data.message || err.response.data.Message)) ||
        err.message ||
        fallback
      )
    },

    marcarApiOffline() {
      this.apiOfflineDisponible = false
      if (!this.avisoApiOffline) {
        this.avisoApiOffline = true
        this.validarErrorDirecto(
          'Material offline: no se pudo conectar con el API. Verifica que api-rh esté desplegado en el servidor y ejecuta pm2 reload api-rh.'
        )
      }
    },

    es404(err) {
      return !!(err && err.response && err.response.status === 404)
    },

    async httpGetConFallback(rutas, config) {
      let ultimoError = null
      for (const ruta of rutas) {
        try {
          return await this.$http.get(ruta, config)
        } catch (err) {
          ultimoError = err
          if (this.es404(err)) continue
          throw err
        }
      }
      throw ultimoError || new Error('Ruta no encontrada')
    },

    async recargarDatosOffline() {
      try {
        await this.cargarCatalogos()
      } catch (e) {
        console.warn('[MaterialOffline] recargar catálogos:', e)
      }
      try {
        await this.cargarRegistros()
      } catch (e) {
        console.warn('[MaterialOffline] recargar registros:', e)
      }
    },

    async inicializar() {
      this.cargar = true
      try {
        await this.cargarPlanteles().catch((e) => console.warn('[MaterialOffline] planteles:', e))
        await this.cargarCatalogos()
        await this.cargarRegistros()
      } catch (e) {
        console.warn('[MaterialOffline] inicializar:', e)
      } finally {
        this.cargar = false
      }
    },

    async cargarRegistros() {
      const params = this.filtroTipo ? { params: { tipo: this.filtroTipo } } : {}
      try {
        const res = await this.httpGetConFallback(
          ['mkt.offline.list.registros', 'mkt.offline.registros'],
          params
        )
        this.registros = res.data || []
        this.apiOfflineDisponible = true
        this.avisoApiOffline = false
      } catch (err) {
        if (this.es404(err)) {
          this.registros = []
          this.marcarApiOffline()
          return
        }
        throw err
      }
    },

    async cargarCatalogos() {
      const params = this.filtroTipo ? { params: { tipo: this.filtroTipo } } : {}

      try {
        const res = await this.httpGetConFallback(
          ['mkt.offline.list.catalogos', 'mkt.offline.catalogos'],
          params
        )
        const data = res.data || {}
        this.escuelas = data.escuelas || []
        this.activos = data.materiales || data.activos || []
        this.proveedores = data.proveedores || []
        this.apiOfflineDisponible = true
        this.avisoApiOffline = false
        return true
      } catch (err) {
        if (!this.es404(err)) throw err
      }

      const results = await Promise.allSettled([
        this.httpGetConFallback(['mkt.offline.list.escuelas', 'mkt.offline.escuelas.list'], params),
        this.httpGetConFallback(['mkt.offline.list.materiales', 'mkt.offline.materiales.list']),
        this.httpGetConFallback(['mkt.offline.list.proveedores', 'mkt.offline.proveedores.list']),
      ])

      const ok = results.filter((r) => r.status === 'fulfilled').length
      if (!ok) {
        this.escuelas = []
        this.activos = []
        this.proveedores = []
        this.marcarApiOffline()
        return false
      }

      if (results[0].status === 'fulfilled') this.escuelas = results[0].value.data || []
      if (results[1].status === 'fulfilled') this.activos = results[1].value.data || []
      if (results[2].status === 'fulfilled') this.proveedores = results[2].value.data || []
      this.apiOfflineDisponible = true
      this.avisoApiOffline = false
      return true
    },

    async cargarPlanteles() {
      const res = await this.$http.get('planteles.activos')
      this.planteles = res.data || []
    },

    formatearPrecio(val) {
      if (val == null || val === '') return '—'
      return Number(val).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })
    },

    formatearFecha(val) {
      if (!val) return '—'
      const d = new Date(val)
      return isNaN(d.getTime()) ? val : d.toLocaleString('es-MX')
    },

    formatearFechaHistorial(val) {
      if (!val) return '—'
      const d = new Date(val)
      if (isNaN(d.getTime())) return val
      const hoy = new Date()
      const ayer = new Date(hoy)
      ayer.setDate(ayer.getDate() - 1)
      const hora = d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
      if (d.toDateString() === hoy.toDateString()) return 'Hoy, ' + hora
      if (d.toDateString() === ayer.toDateString()) return 'Ayer, ' + hora
      return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }) + ', ' + hora
    },

    formatearValorHistorial(val, campo) {
      if (val == null || val === '') return '—'
      const s = String(val)
      const c = (campo || '').toLowerCase()
      if (c.includes('precio') && !isNaN(Number(s))) {
        return Number(s).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })
      }
      return s
    },

    iconoHistorial(item) {
      const c = (item.campo || '').toLowerCase()
      if (c.includes('precio')) return 'mdi-currency-usd'
      if (c.includes('link') || c.includes('archivo') || c.includes('drive')) return 'mdi-google-drive'
      if (c.includes('garant')) return 'mdi-shield-check'
      if (c.includes('manten')) return 'mdi-wrench'
      if (c.includes('nota')) return 'mdi-note-text'
      if (c.includes('registro')) return 'mdi-plus-circle'
      if (c.includes('activo') || c.includes('medida')) return 'mdi-image-area'
      if (c.includes('proveedor')) return 'mdi-truck'
      return 'mdi-pencil'
    },

    tipoHistorial(item) {
      const c = (item.campo || '').toLowerCase()
      if (c.includes('precio')) return 'precio'
      if (c.includes('archivo') || c.includes('link')) return 'archivo'
      if (c.includes('registro')) return 'alta'
      return 'edit'
    },

    inicialesUsuario(nombre) {
      if (!nombre) return '?'
      const partes = String(nombre).trim().split(/\s+/).filter(Boolean)
      if (partes.length >= 2) return (partes[0][0] + partes[1][0]).toUpperCase()
      return partes[0].substring(0, 2).toUpperCase()
    },

    mantenimientoUrgente(fecha) {
      if (!fecha) return false
      const f = new Date(fecha + 'T00:00:00')
      if (isNaN(f.getTime())) return false
      const hoy = new Date()
      hoy.setHours(0, 0, 0, 0)
      const limite = new Date(hoy)
      limite.setDate(limite.getDate() + 30)
      return f >= hoy && f <= limite
    },

    linkDrive(url) {
      if (!url) return ''
      const u = String(url).trim()
      if (!u) return ''
      if (/^https?:\/\//i.test(u)) return u
      return 'https://' + u
    },

    normalizarActivoArchivo(item) {
      const out = { ...item }
      if (out.archivo_url) {
        out.archivo_url = this.linkDrive(out.archivo_url)
        if (!out.archivo_tipo) {
          out.archivo_tipo = /\.pdf(\?|$)/i.test(out.archivo_url) ? 'pdf' : 'foto'
        }
      } else {
        out.archivo_url = null
        out.archivo_tipo = out.archivo_tipo || null
      }
      return out
    },

    abrirRegistro(item) {
      if (!this.puedeEditar) {
        return this.validarErrorDirecto('Sin permiso para editar Material offline')
      }
      if (item) {
        this.registroIndex = this.registros.indexOf(item)
        this.registroItem = {
          ...defaultRegistro(),
          ...item,
          comentario_historial: '',
        }
      } else {
        this.registroIndex = -1
        this.registroItem = defaultRegistro()
      }
      this.dialogRegistro = true
    },

    cerrarRegistro() {
      this.dialogRegistro = false
      this.registroItem = defaultRegistro()
      this.registroIndex = -1
    },

    onEscuelaRegistroChange() {
      this.registroItem.id_activo = null
    },

    async guardarRegistro() {
      if (!this.puedeEditar) {
        return this.validarErrorDirecto('Sin permiso para editar Material offline')
      }
      if (!this.registroItem.id_escuela || !this.registroItem.id_activo) {
        return this.validarErrorDirecto('Seleccione escuela y activo')
      }
      this.cargar = true
      const payload = { ...this.registroItem, id_usuario: this.idUsuario }
      try {
        if (this.registroIndex > -1) {
          await this.$http.put(`mkt.offline.registros.update/${payload.id_registro}`, payload)
          this.validarSuccess('Registro actualizado')
        } else {
          await this.$http.post('mkt.offline.registros.add', payload)
          this.validarSuccess('Registro guardado')
        }
        this.cerrarRegistro()
        await this.recargarDatosOffline()
      } catch (e) {
        this.validarError(this.errMsgApi(e, 'Error al guardar registro'))
      } finally {
        this.cargar = false
      }
    },

    async abrirHistorial(item) {
      this.historialRegistro = item
      this.cargar = true
      try {
        const res = await this.$http.get(`mkt.offline.historial/${item.id_registro}`)
        this.historial = res.data || []
        this.dialogHistorial = true
      } catch (e) {
        this.validarError(this.errMsgApi(e, 'Error al cargar historial'))
      } finally {
        this.cargar = false
      }
    },

    abrirCatalogo() {
      if (!this.puedeEditar) {
        return this.validarErrorDirecto('Sin permiso para editar Material offline')
      }
      this.tabCatalogo = 0
      this.dialogCatalogo = true
      this.cargarCatalogos()
    },

    nuevaEscuela() {
      this.escuelaIndex = -1
      this.escuelaItem = { id_escuela: 0, tipo: this.filtroTipo || 'FAST', id_plantel: null, nombre_sucursal: '', activo_sn: 1 }
      this.dialogEscuela = true
    },

    editarEscuela(item) {
      this.escuelaIndex = this.escuelas.indexOf(item)
      this.escuelaItem = { ...item }
      this.dialogEscuela = true
    },

    onPlantelSeleccionado(id) {
      if (this.escuelaIndex === -1) {
        const duplicada = this.escuelas.find(
          (e) => e.tipo === this.escuelaItem.tipo && Number(e.id_plantel) === Number(id)
        )
        if (duplicada) {
          this.validarErrorDirecto('Este plantel ya está registrado como escuela')
          this.escuelaItem.id_plantel = null
          this.escuelaItem.nombre_sucursal = ''
          return
        }
      }
      const p = this.planteles.find((x) => Number(x.id_plantel) === Number(id))
      this.escuelaItem.nombre_sucursal = p ? p.plantel : ''
    },

    async guardarEscuela() {
      if (!this.escuelaItem.tipo || !this.escuelaItem.id_plantel) {
        return this.validarErrorDirecto('Seleccione tipo y plantel')
      }
      if (this.escuelaIndex === -1) {
        const duplicada = this.escuelas.find(
          (e) => e.tipo === this.escuelaItem.tipo && Number(e.id_plantel) === Number(this.escuelaItem.id_plantel)
        )
        if (duplicada) {
          return this.validarErrorDirecto('Este plantel ya está registrado como escuela')
        }
      }
      this.cargar = true
      const payload = { ...this.escuelaItem, id_usuario: this.idUsuario }
      try {
        if (this.escuelaIndex > -1) {
          await this.$http.put(`mkt.offline.escuelas.update/${payload.id_escuela}`, payload)
        } else {
          await this.$http.post('mkt.offline.escuelas.add', payload)
        }

        this.validarSuccess('Escuela guardada')
        this.dialogEscuela = false
        await this.recargarDatosOffline()
      } catch (e) {
        const status = e && e.response && e.response.status
        if (status === 409) {
          this.validarErrorDirecto(
            (e.response.data && e.response.data.message) || 'Este plantel ya está registrado como escuela'
          )
          return
        }
        this.validarError(this.errMsgApi(e, 'Error al guardar escuela'))
      } finally {
        this.cargar = false
      }
    },

    nuevoActivo() {
      this.activoIndex = -1
      this.activoItem = { id_activo: 0, id_escuela: null, nombre: '', medidas: '', archivo_url: '', archivo_tipo: 'foto', activo_sn: 1, id_proveedor_existente: null, comentario_historial: '' }
      this.asignarProveedorActivo = false
      this.proveedorWizard = { nombre: '', telefono: '', numero_cuenta: '', nota: '' }
      this.dialogActivo = true
    },

    editarActivo(item) {
      this.activoIndex = this.activos.indexOf(item)
      this.activoItem = { ...item, id_proveedor_existente: null }
      this.dialogActivo = true
    },

    async guardarActivo() {
      if (!this.activoItem.id_escuela || !this.activoItem.nombre) {
        return this.validarErrorDirecto('Seleccione escuela y nombre del activo')
      }
      this.cargar = true
      const payload = this.normalizarActivoArchivo({ ...this.activoItem, id_usuario: this.idUsuario })
      try {
        if (this.activoIndex > -1) {
          await this.$http.put(`mkt.offline.materiales.update/${payload.id_activo}`, payload)
        } else {
          await this.$http.post('mkt.offline.materiales.add', payload)
          if (this.asignarProveedorActivo && !this.activoItem.id_proveedor_existente && this.proveedorWizard.nombre) {
            await this.$http.post('mkt.offline.proveedores.add', {
              ...this.proveedorWizard,
              id_usuario: this.idUsuario,
            })
          }
        }
        this.validarSuccess('Activo guardado')
        this.dialogActivo = false
        await this.recargarDatosOffline()
      } catch (e) {
        this.validarError(this.errMsgApi(e, 'Error al guardar activo'))
      } finally {
        this.cargar = false
      }
    },

    nuevoProveedor() {
      this.proveedorIndex = -1
      this.proveedorItem = { id_proveedor: 0, nombre: '', telefono: '', numero_cuenta: '', nota: '', activo_sn: 1 }
      this.dialogProveedor = true
    },

    editarProveedor(item) {
      this.proveedorIndex = this.proveedores.indexOf(item)
      this.proveedorItem = { ...item }
      this.dialogProveedor = true
    },

    async guardarProveedor() {
      if (!this.proveedorItem.nombre) {
        return this.validarErrorDirecto('El nombre del proveedor es requerido')
      }
      this.cargar = true
      const payload = { ...this.proveedorItem, id_usuario: this.idUsuario }
      try {
        if (this.proveedorIndex > -1) {
          await this.$http.put(`mkt.offline.proveedores.update/${payload.id_proveedor}`, payload)
        } else {
          await this.$http.post('mkt.offline.proveedores.add', payload)
        }
        this.validarSuccess('Proveedor guardado')
        this.dialogProveedor = false
        await this.recargarDatosOffline()
      } catch (e) {
        this.validarError(this.errMsgApi(e, 'Error al guardar proveedor'))
      } finally {
        this.cargar = false
      }
    },
  },
}
</script>

<style scoped>
/* Paleta azul suave */
.mkt-page {
  background: linear-gradient(180deg, #e8f0fa 0%, #f4f8fd 120px, #f4f8fd 100%);
  min-height: calc(100vh - 64px);
}

.mkt-hero-avatar {
  box-shadow: 0 6px 20px rgba(30, 136, 229, 0.2);
}

.mkt-stat-total { color: #1565c0; }
.mkt-stat-fast { color: #1976d2; }
.mkt-stat-oficina { color: #1e88e5; }
.mkt-stat-warm { color: #42a5f5; }

.mkt-text-price { color: #1565c0; }

.mkt-chip-fast {
  background-color: #e3f2fd !important;
  color: #1565c0 !important;
}

.mkt-chip-oficina {
  background-color: #e1f0fa !important;
  color: #1976d2 !important;
}

.mkt-chip-tipo {
  font-weight: 500;
}

.mkt-chip-mant {
  background-color: #eceff1 !important;
  color: #546e7a !important;
}

.mkt-chip-mant--soon {
  background-color: #bbdefb !important;
  color: #0d47a1 !important;
}

.mkt-kpi {
  background: #fff;
  border: 1px solid rgba(25, 118, 210, 0.12);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.mkt-kpi:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(25, 118, 210, 0.12);
}

.mkt-kpi-icon {
  opacity: 0.45;
}

.mkt-panel {
  background: #fff;
  border: 1px solid rgba(25, 118, 210, 0.1);
  overflow: hidden;
}

.mkt-panel-toolbar {
  background: #f5f9fd;
}

.mkt-search {
  max-width: 320px;
  flex: 1 1 220px;
}

.mkt-filter-chips >>> .v-chip.mkt-filter-chip {
  background-color: #e3f2fd !important;
  color: #1565c0 !important;
  font-weight: 500;
  border: 1px solid #90caf9 !important;
}

.mkt-filter-chips >>> .v-chip.mkt-filter-chip .v-chip__content {
  color: #1565c0 !important;
}

.mkt-filter-chips >>> .v-chip.mkt-filter-chip.v-chip--active {
  background-color: #1976d2 !important;
  border-color: #1976d2 !important;
  color: #ffffff !important;
}

.mkt-filter-chips >>> .v-chip.mkt-filter-chip.v-chip--active .v-chip__content {
  color: #ffffff !important;
}

.mkt-table >>> thead th {
  background: #e8f0fa !important;
  color: #1565c0 !important;
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.mkt-table >>> tbody tr:hover {
  background: #f3f8fe !important;
}

.mkt-table--nested {
  border: 1px solid rgba(25, 118, 210, 0.1);
}

.mkt-nota-cell {
  max-width: 130px;
}

.mkt-dialog-header {
  background: linear-gradient(135deg, #1565c0 0%, #42a5f5 100%);
}

.mkt-dialog-header--catalog {
  background: linear-gradient(135deg, #1976d2 0%, #64b5f6 100%);
}

.mkt-dialog-header--history {
  background: linear-gradient(135deg, #1565c0 0%, #42a5f5 100%);
}

/* Historial modal */
.mkt-historial-summary {
  background: linear-gradient(180deg, #f0f7ff 0%, #fafcff 100%);
  border-bottom: 1px solid rgba(25, 118, 210, 0.08);
}

.mkt-historial-body {
  background: #f8fbfe;
  max-height: 55vh;
  overflow-y: auto;
}

.mkt-timeline {
  position: relative;
}

.mkt-timeline-item {
  display: flex;
  gap: 16px;
  margin-bottom: 0;
  padding-bottom: 20px;
}

.mkt-timeline-item--last {
  padding-bottom: 0;
}

.mkt-timeline-rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 32px;
  flex-shrink: 0;
}

.mkt-timeline-dot {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.25);
}

.mkt-timeline-dot--edit { background: #1976d2; }
.mkt-timeline-dot--precio { background: #1565c0; }
.mkt-timeline-dot--archivo { background: #1e88e5; }
.mkt-timeline-dot--alta { background: #42a5f5; }

.mkt-timeline-line {
  width: 2px;
  flex: 1;
  min-height: 24px;
  background: linear-gradient(180deg, #90caf9 0%, #e3f2fd 100%);
  margin-top: 4px;
}

.mkt-timeline-card {
  flex: 1;
  min-width: 0;
  padding: 14px 16px !important;
  border-color: rgba(25, 118, 210, 0.15) !important;
  background: #fff !important;
  transition: box-shadow 0.2s ease;
}

.mkt-timeline-card:hover {
  box-shadow: 0 4px 16px rgba(25, 118, 210, 0.1) !important;
}

.mkt-historial-diff {
  display: flex;
  align-items: stretch;
  flex-wrap: wrap;
  gap: 8px;
}

.mkt-diff-box {
  flex: 1;
  min-width: 120px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid transparent;
}

.mkt-diff-box--old {
  background: #fafafa;
  border-color: #eeeeee;
}

.mkt-diff-box--new {
  background: #e3f2fd;
  border-color: #bbdefb;
}

.mkt-diff-label {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #78909c;
  font-weight: 600;
  margin-bottom: 4px;
}

.mkt-diff-value {
  font-size: 0.875rem;
  word-break: break-word;
  color: #37474f;
}

.mkt-diff-box--new .mkt-diff-value {
  color: #1565c0;
  font-weight: 500;
}

.mkt-historial-comentario {
  font-style: italic;
  line-height: 1.4;
  margin-top: 2px;
}

.min-width-0 {
  min-width: 0;
}
</style>
